import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import { RowMenu } from '../ui/RowMenu';
import { FINANCE_TABS, visibleFinanceTabs } from './financeTabs';
import './finance.css';

const tabOf = (pathname: string) => FINANCE_TABS.find((t) => pathname === t.path || pathname.startsWith(`${t.path}/`));

/** Route guard of one tab: the same rule as the tab's visibility. */
export const canSeeFinanceTab = (key: string) => (me: Me) => FINANCE_TABS.find((t) => t.key === key)?.visible(me) ?? false;

/**
 * "Finanse" (F32, spec E3 §7): one entry in the sidebar; tabs at the top as segments, the rarer ones under
 * "Więcej ▾". Only the tabs the role may view (and of modules that exist). On a phone the row scrolls sideways.
 */
export function FinanceLayout() {
  const { me } = useMe();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  if (!me) return null;
  const tabs = visibleFinanceTabs(me);
  const main = tabs.filter((t) => !t.more);
  const more = tabs.filter((t) => t.more);
  const activeMore = more.find((t) => t === tabOf(pathname));
  return (
    <div className="finance-layout" data-feature-id="finance.tabs">
      <div className="page-head">
        <h1>Finanse</h1>
      </div>
      <nav className="finance-tabs" aria-label="Zakładki Finansów">
        {main.length > 0 && (
          <div className="segment">
            {main.map((t) => (
              <NavLink key={t.key} to={t.path}>
                {t.label}
              </NavLink>
            ))}
          </div>
        )}
        {more.length > 0 && (
          <span className={activeMore ? 'finance-more on' : 'finance-more'}>
            <RowMenu
              label={activeMore ? `Więcej: ${activeMore.label}` : 'Więcej'}
              text={`${activeMore?.label ?? 'Więcej'} ▾`}
              actions={more.map((t) => ({ label: t.label, onSelect: () => navigate(t.path) }))}
            />
          </span>
        )}
      </nav>
      <Outlet />
    </div>
  );
}

/** `/finanse`: the first tab the role may see (the query, e.g. `?miesiac=`, goes along). */
export function FinanceIndex() {
  const { me } = useMe();
  const { search } = useLocation();
  if (!me) return null;
  const first = visibleFinanceTabs(me)[0];
  return <Navigate to={first ? `${first.path}${search}` : '/'} replace />;
}

/** Old links `/cenniki/…` (bookmarks, links in e-mails) land on the same page under Finanse. */
export function LegacyPricingRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`/finanse${pathname}${search}${hash}`} replace />;
}
